import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type RefObject,
} from 'react';

/**
 * PD-16574 — the storefront's Cloudflare Turnstile widget, rendered the way
 * docs/claude/forms/01-design.md §3b requires of every page that posts a Workd form:
 *
 * - `api.js?render=explicit` loads once per page, and only when a form needs it;
 * - the widget renders only once its container is on screen (a widget rendered out of
 *   sight times out without a token, and in interaction-only mode there is nothing
 *   visible to retry), with the form key as its `action` so the server can check it;
 * - `getToken` waits briefly for a token the widget is still working on;
 * - `reset` runs after every submit attempt, because tokens are single-use, and the
 *   widget resets itself when a token expires;
 * - a script that cannot load, or a widget error, is reported so the page can say so.
 */

interface TurnstileRenderOptionsModel {
	sitekey: string;
	action: string;
	theme: 'light' | 'dark' | 'auto';
	appearance: 'always' | 'execute' | 'interaction-only';
	callback: (token: string) => void;
	'expired-callback': () => void;
	'error-callback': () => boolean;
}

interface TurnstileApiModel {
	render: (
		container: HTMLElement,
		options: TurnstileRenderOptionsModel,
	) => string;
	reset: (widgetId: string) => void;
	remove: (widgetId: string) => void;
}

declare global {
	interface Window {
		turnstile?: TurnstileApiModel;
	}
}

const SCRIPT_URL =
	'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<TurnstileApiModel> | null = null;

function loadTurnstile(): Promise<TurnstileApiModel> {
	if (window.turnstile != null) {
		return Promise.resolve(window.turnstile);
	}
	scriptPromise ??= new Promise<TurnstileApiModel>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = SCRIPT_URL;
		script.async = true;
		script.onload = (): void => {
			if (window.turnstile != null) {
				resolve(window.turnstile);
			} else {
				reject(new Error('Turnstile loaded without its API'));
			}
		};
		script.onerror = (): void => {
			// Let a later form try again (a network blip, a blocker switched off).
			scriptPromise = null;
			script.remove();
			reject(new Error('Turnstile could not load'));
		};
		document.head.appendChild(script);
	});
	return scriptPromise;
}

/** How long a submit waits for a token the widget has not produced yet. */
const TOKEN_WAIT_MS = 5000;

export interface TurnstileControlModel {
	/** Put this on the element the widget draws into. */
	containerRef: RefObject<HTMLDivElement | null>;
	/** The current token, waiting up to five seconds for one; null when none came. */
	getToken: () => Promise<string | null>;
	/** Discards the token and starts a fresh check; call after every submit attempt. */
	reset: () => void;
	/** True when the script would not load or the widget reported an error. */
	failed: boolean;
}

export function useTurnstile({
	siteKey,
	action,
	theme,
}: {
	/** Null when the form has spam protection off: nothing loads or renders. */
	siteKey: string | null;
	action: string;
	theme: 'light' | 'dark';
}): TurnstileControlModel {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const widgetIdRef = useRef<string | null>(null);
	const tokenRef = useRef<string | null>(null);
	const waitersRef = useRef<Array<(token: string) => void>>([]);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		const container = containerRef.current;
		if (siteKey == null || container == null) {
			return;
		}
		let cancelled = false;
		let api: TurnstileApiModel | null = null;

		const render = async (): Promise<void> => {
			try {
				api = await loadTurnstile();
			} catch {
				if (!cancelled) {
					setFailed(true);
				}
				return;
			}
			if (cancelled || widgetIdRef.current != null) {
				return;
			}
			widgetIdRef.current = api.render(container, {
				sitekey: siteKey,
				action,
				theme,
				appearance: 'interaction-only',
				callback: (token) => {
					tokenRef.current = token;
					setFailed(false);
					const waiters = waitersRef.current;
					waitersRef.current = [];
					for (const resolve of waiters) {
						resolve(token);
					}
				},
				'expired-callback': () => {
					tokenRef.current = null;
					if (widgetIdRef.current != null) {
						api?.reset(widgetIdRef.current);
					}
				},
				'error-callback': () => {
					tokenRef.current = null;
					setFailed(true);
					// Handled here; stops Cloudflare logging the same error to the console.
					return true;
				},
			});
		};

		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				observer.disconnect();
				void render();
			}
		});
		observer.observe(container);

		return (): void => {
			cancelled = true;
			observer.disconnect();
			if (widgetIdRef.current != null) {
				api?.remove(widgetIdRef.current);
				widgetIdRef.current = null;
			}
			tokenRef.current = null;
		};
	}, [siteKey, action, theme]);

	const getToken = useCallback(async (): Promise<string | null> => {
		if (tokenRef.current != null) {
			return tokenRef.current;
		}
		let resolveWaiter: ((token: string) => void) | null = null;
		const token = await Promise.race([
			new Promise<string>((resolve) => {
				resolveWaiter = resolve;
				waitersRef.current.push(resolve);
			}),
			new Promise<null>((resolve) => {
				setTimeout(() => {
					resolve(null);
				}, TOKEN_WAIT_MS);
			}),
		]);
		waitersRef.current = waitersRef.current.filter(
			(waiter) => waiter !== resolveWaiter,
		);
		return token;
	}, []);

	const reset = useCallback((): void => {
		tokenRef.current = null;
		if (widgetIdRef.current != null) {
			window.turnstile?.reset(widgetIdRef.current);
		}
	}, []);

	return { containerRef, getToken, reset, failed };
}
